import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { useI18n } from '../../context/useI18n';
import { useAuth } from '../../context/useAuth';
import { userRoles } from '../dashboard/roles';
import { safeRemoveNode } from '../../utils/safeDom';
import {
  isD28dPlatformGym,
  isPlatformTemplateGym,
} from '../../utils/organizationView';
import './d28d-admin-surfaces.css';

const TABS = {
  USERS: 'users',
  SESSIONS: 'sessions',
  CLASSES: 'classes',
};

export default function D28dCoachTracking({ onBack = null }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const roles = userRoles(user);
  const isGymAdminView = roles.some((r) => ['admin_marca', 'admin_gimnasio', 'admin_gym', 'admin_sede'].includes(r));
  const canFilterByGym = roles.some((r) => ['super_admin', 'admin_d28d'].includes(r));
  const pageTitle = isGymAdminView
    ? t('d28d.tracking.title_gym', 'Seguimiento')
    : t('d28d.tracking.title', 'Seguimiento D28D');
  const pageSubtitle = isGymAdminView
    ? t('d28d.tracking.subtitle_gym', 'Usuarios de tu gimnasio y asistencia a clases en vivo.')
    : t('d28d.tracking.subtitle', 'Usuarios de tus clases y registro de asistencia.');
  const kpiClassesLabel = isGymAdminView
    ? t('d28d.tracking.kpi_classes_gym', 'Clases del gym')
    : t('d28d.tracking.kpi_classes', 'Mis clases');
  const today = new Date().toISOString().split('T')[0];
  const monthAgo = new Date(Date.now() - 28 * 86400000).toISOString().split('T')[0];
  const [tab, setTab] = useState(TABS.USERS);
  const [startDate, setStartDate] = useState(monthAgo);
  const [endDate, setEndDate] = useState(today);
  const [gymFilterId, setGymFilterId] = useState('');
  const [gymOptions, setGymOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [overview, setOverview] = useState(null);
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [trafficNotify, setTrafficNotify] = useState(false);
  const [trafficJobEnabled, setTrafficJobEnabled] = useState(null);
  const [trafficBusy, setTrafficBusy] = useState(false);
  const [trafficMsg, setTrafficMsg] = useState('');
  // admin_sede: sin panel de avisos de semáforo (solo plataforma / gym padre).
  const canManageTraffic = !roles.includes('admin_sede') && roles.some((r) => [
    'super_admin', 'admin_d28d',
    'admin_marca', 'admin_gimnasio', 'admin_gym', 'admin_gym_virtual',
  ].includes(r));

  const loadTrafficConfig = useCallback(async () => {
    if (!canManageTraffic) return;
    try {
      const resp = await api.get('/d28d/progress/traffic-light-config');
      const data = resp.data?.data || {};
      setTrafficNotify(Boolean(data.notifications?.traffic_light_notify));
      setTrafficJobEnabled(data.job_enabled !== false);
    } catch {
      setTrafficNotify(false);
      setTrafficJobEnabled(null);
    }
  }, [canManageTraffic]);

  const saveTrafficNotify = async (checked) => {
    setTrafficBusy(true);
    setTrafficMsg('');
    try {
      const resp = await api.put('/d28d/progress/traffic-light-config', {
        notifications: { traffic_light_notify: checked },
      });
      const data = resp.data?.data || {};
      setTrafficNotify(Boolean(data.notifications?.traffic_light_notify));
      setTrafficJobEnabled(data.job_enabled !== false);
      setTrafficMsg(t('d28d.tracking.traffic_saved', 'Guardado'));
    } catch {
      setTrafficMsg(t('d28d.tracking.traffic_save_error', 'No se pudo guardar'));
    } finally {
      setTrafficBusy(false);
    }
  };

  const loadGymOptions = useCallback(async () => {
    if (!canFilterByGym) return;
    try {
      const resp = await api.get('/gyms');
      const list = Array.isArray(resp.data?.data) ? resp.data.data : (Array.isArray(resp.data) ? resp.data : []);
      setGymOptions(
        list
          .filter((g) => !isPlatformTemplateGym(g))
          .map((g) => ({
            id: Number(g.id),
            nombre: isD28dPlatformGym(g)
              ? t('gyms.kind_d28d', 'D28D')
              : (g.nombre || g.name || `Gimnasio ${g.id}`),
            parent_gym_id: g.parent_gym_id ?? null,
          }))
          .filter((g) => Number.isFinite(g.id))
          .sort((a, b) => {
            const aD28d = a.nombre === 'D28D' || a.nombre === t('gyms.kind_d28d', 'D28D') ? 0 : 1;
            const bD28d = b.nombre === 'D28D' || b.nombre === t('gyms.kind_d28d', 'D28D') ? 0 : 1;
            if (aD28d !== bD28d) return aD28d - bD28d;
            const ap = a.parent_gym_id == null ? 0 : 1;
            const bp = b.parent_gym_id == null ? 0 : 1;
            if (ap !== bp) return ap - bp;
            return String(a.nombre).localeCompare(String(b.nombre), 'es');
          }),
      );
    } catch {
      setGymOptions([]);
    }
  }, [canFilterByGym, t]);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { start_date: startDate, end_date: endDate };
      if (canFilterByGym && gymFilterId) params.gym_id = gymFilterId;
      const resp = await api.get('/d28d/coach/overview', { params });
      setOverview(resp.data?.data || null);
    } catch {
      setError(t('d28d.tracking.load_error', 'No se pudo cargar el seguimiento.'));
      setOverview(null);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, gymFilterId, canFilterByGym, t]);

  const loadAttendance = useCallback(async () => {
    try {
      const resp = await api.get('/live-classes/admin/attendance');
      setAttendanceRows(resp.data?.data || []);
    } catch {
      setAttendanceRows([]);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    loadTrafficConfig();
  }, [loadTrafficConfig]);

  useEffect(() => {
    loadGymOptions();
  }, [loadGymOptions]);

  useEffect(() => {
    if (tab === TABS.CLASSES) loadAttendance();
  }, [tab, loadAttendance]);

  const kpis = overview?.kpis || {};
  const users = overview?.users || [];
  const sessions = overview?.sessions || [];

  const gymSelectOptions = useMemo(() => {
    const fromApi = gymOptions;
    const fromOverview = (overview?.gyms || []).map((g) => ({
      id: Number(g.id),
      nombre: g.nombre,
      parent_gym_id: null,
    }));
    const byId = new Map();
    [...fromApi, ...fromOverview].forEach((g) => {
      if (g?.id != null && Number.isFinite(Number(g.id))) byId.set(Number(g.id), g);
    });
    return Array.from(byId.values()).sort((a, b) => String(a.nombre).localeCompare(String(b.nombre), 'es'));
  }, [gymOptions, overview?.gyms]);

  const exportUsersCsv = () => {
    const header = 'Usuario,Email,Asistencias,Ultima asistencia,Programa,Gimnasio\n';
    const body = users
      .map((u) => `"${u.nombre}","${u.email}",${u.classes_attended},"${u.last_attendance || ''}","${u.program_name || u.program_id || ''}","${u.gym_name || ''}"`)
      .join('\n');
    downloadCsv(header + body, `${isGymAdminView ? 'gym' : 'd28d'}-seguimiento-usuarios-${endDate}.csv`);
  };

  return (
    <div className="d28d-admin-surface d28d-tracking-view surface-readable dashboard-main-view">
      <header className="dashboard-header page-header">
        <div>
          <h1 className="d28d-page-title">{pageTitle}</h1>
          <p className="d28d-text-muted subtitle">
            {pageSubtitle}
          </p>
        </div>
        {onBack && (
          <button type="button" className="btn-secondary" onClick={onBack}>
            {t('panel.back', '← Volver')}
          </button>
        )}
      </header>

      <section className="ui-light-panel ui-light-panel--muted d28d-tracking-filters mb-4">
        <h2 className="d28d-tracking-filters__title">{t('progress.range_filters', 'Rango y filtros')}</h2>
        <div className="d28d-tracking-filters__grid">
          <label className="block">
            <span className="ui-field__label">{t('progress.start', 'Inicio')}</span>
            <input
              type="date"
              className="ui-input"
              value={startDate}
              max={endDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="ui-field__label">{t('progress.end', 'Fin')}</span>
            <input
              type="date"
              className="ui-input"
              value={endDate}
              min={startDate}
              max={today}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </label>
          {canFilterByGym && (
            <label className="block">
              <span className="ui-field__label">{t('users.gym', 'Gimnasio')}</span>
              <select
                className="ui-input"
                value={gymFilterId}
                onChange={(e) => setGymFilterId(e.target.value)}
              >
                <option value="">{t('d28d.tracking.all_gyms', 'Todos los gimnasios')}</option>
                {gymSelectOptions.map((g) => (
                  <option key={g.id} value={String(g.id)}>
                    {g.parent_gym_id ? `↳ ${g.nombre}` : g.nombre}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        <button type="button" className="btn-primary mt-3" onClick={loadOverview} disabled={loading}>
          {loading ? t('common.loading', 'Cargando…') : t('d28d.tracking.refresh', 'Actualizar')}
        </button>
      </section>

      {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

      {canManageTraffic && (
        <section className="card ui-light-panel p-4 space-y-2 mb-4">
          <h3 className="font-semibold text-stone-900">
            {t('d28d.tracking.traffic_title', 'Avisos de semáforo (adherencia)')}
          </h3>
          <label className="flex items-center gap-2 text-sm text-stone-800">
            <input
              type="checkbox"
              checked={trafficNotify}
              disabled={trafficBusy}
              onChange={(e) => saveTrafficNotify(e.target.checked)}
            />
            {t(
              'd28d.tracking.traffic_optin',
              'Enviar avisos in_app/email de semáforo a usuarios D28D (opt-in)',
            )}
          </label>
          <p className="text-xs text-stone-500">
            {t(
              'd28d.tracking.traffic_hint',
              'Desactivado por defecto. Requiere job D28D_TRAFFIC_LIGHT_JOB en el servidor.',
            )}
            {trafficJobEnabled === false ? ' · Job apagado en este entorno.' : null}
            {trafficMsg ? ` · ${trafficMsg}` : null}
          </p>
        </section>
      )}

      <div className="d28d-tracking-kpi-grid mb-6">
        <KpiCard label={t('d28d.tracking.kpi_users', 'Usuarios')} value={kpis.total_users ?? 0} />
        <KpiCard label={t('d28d.tracking.kpi_sessions', 'Asistencias')} value={kpis.total_sessions ?? 0} />
        <KpiCard label={kpiClassesLabel} value={kpis.total_classes ?? 0} />
        <KpiCard
          label={t('d28d.tracking.kpi_avg', 'Promedio / clase')}
          value={kpis.avg_attendees_per_class ?? 0}
        />
      </div>

      <div className="d28d-tracking-tabs mb-4">
        {[
          [TABS.USERS, t('d28d.tracking.tab_users', 'Mis usuarios')],
          [TABS.SESSIONS, t('d28d.tracking.tab_sessions', 'Historial')],
          [TABS.CLASSES, t('d28d.tracking.tab_classes', 'Por clase')],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`d28d-tracking-tab ${tab === id ? 'd28d-tracking-tab--active' : ''}`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading && tab === TABS.USERS && <p className="d28d-text-muted">{t('common.loading', 'Cargando…')}</p>}

      {tab === TABS.USERS && !loading && (
        <section>
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-bold text-lg">{t('d28d.tracking.users_heading', 'Participantes en tus clases')}</h3>
            {users.length > 0 && (
              <button type="button" className="btn-secondary text-sm" onClick={exportUsersCsv}>
                {t('progress.export_csv', 'Exportar CSV')}
              </button>
            )}
          </div>
          {users.length === 0 ? (
            <EmptyState message={t('d28d.tracking.no_users', 'Aún no hay asistencias registradas en el rango.')} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-stone-200 rounded-xl overflow-hidden">
                <thead className="bg-stone-100">
                  <tr>
                    <th className="p-2 text-left">Usuario</th>
                    <th className="p-2 text-left">Email</th>
                    <th className="p-2 text-right">Asistencias</th>
                    <th className="p-2 text-left">Última</th>
                    <th className="p-2 text-left">Programa</th>
                    <th className="p-2 text-left">{t('users.gym', 'Gimnasio')}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-t border-stone-100">
                      <td className="p-2 font-semibold">{u.nombre}</td>
                      <td className="p-2 text-stone-600">{u.email}</td>
                      <td className="p-2 text-right">{u.classes_attended}</td>
                      <td className="p-2 text-stone-600">{formatDate(u.last_attendance)}</td>
                      <td className="p-2 text-stone-600">{u.program_name || u.program_id || '—'}</td>
                      <td className="p-2 text-stone-600">{u.gym_name || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {tab === TABS.SESSIONS && (
        <section className="space-y-3">
          {sessions.length === 0 ? (
            <EmptyState message={t('d28d.tracking.no_sessions', 'Sin sesiones en el rango.')} />
          ) : (
            sessions.map((s, idx) => {
              const u = users.find((x) => x.user_id === s.user_id);
              return (
                <div key={`${s.class_id}-${s.user_id}-${idx}`} className="card p-4 border border-stone-200">
                  <div className="flex flex-wrap justify-between gap-2">
                    <span className="font-bold">{u?.nombre || `Usuario ${s.user_id}`}</span>
                    <span className="text-sm text-stone-500">{formatDate(s.joined_at)}</span>
                  </div>
                  <p className="text-sm text-stone-700 mt-1">{s.class_title}</p>
                  {u?.gym_name ? (
                    <p className="text-xs text-stone-500 mt-1">{u.gym_name}</p>
                  ) : null}
                </div>
              );
            })
          )}
        </section>
      )}

      {tab === TABS.CLASSES && (
        <section className="space-y-4">
          {attendanceRows.length === 0 ? (
            <EmptyState message={t('d28d.tracking.no_classes', 'Sin clases asignadas o sin reporte.')} />
          ) : (
            attendanceRows.map((row) => (
              <div key={row.class_id} className="card p-4 border border-stone-200">
                <div className="flex justify-between flex-wrap gap-2">
                  <h4 className="font-bold">{row.title}</h4>
                  <span className="text-sm text-stone-600">{formatDate(row.start_time)}</span>
                </div>
                <p className="text-sm mt-1">
                  {t('d28d.tracking.attendees', 'Asistentes')}: <strong>{row.total_attendees}</strong>
                </p>
                {row.by_gym?.map((g) => (
                  <div key={g.gym_name} className="mt-2 text-sm">
                    <span className="font-semibold">{g.gym_name}</span> ({g.count})
                    <ul className="list-disc ml-5 text-stone-600">
                      {g.attendees?.map((a) => (
                        <li key={a.user_id}>{a.nombre} — {a.email}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ))
          )}
        </section>
      )}
    </div>
  );
}

function KpiCard({ label, value }) {
  return (
    <div className="d28d-tracking-kpi">
      <span className="d28d-tracking-kpi__label">{label}</span>
      <p className="d28d-tracking-kpi__value">{value}</p>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="d28d-tracking-empty">
      {message}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
    return d.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return String(iso).slice(0, 16);
  }
}

function downloadCsv(content, filename) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  safeRemoveNode(link);
  URL.revokeObjectURL(url);
}
